/**
 * Two-clock time utilities for the simulator.
 *
 * All times are fractional ET hours (9.5 = 9:30 AM, 16.0 = 4:00 PM).
 * These are pure functions — no side effects, fully testable.
 */

import { DAYS_PER_YEAR, HOURS_PER_YEAR } from "@/lib/constants";

export const MARKET_OPEN_HOUR = 9.5; // 9:30 AM ET
export const MARKET_CLOSE_HOUR = 16; // 4:00 PM ET

/**
 * Snap a fractional ET hour to the nearest 15-minute step.
 * Example: 10.33 → 10.25 (10:15 AM), 10.40 → 10.5 (10:30 AM)
 */
export function snapTo15Min(hour: number): number {
  return Math.round(hour * 4) / 4;
}

/**
 * Compute time-to-expiry (in years) from DTE + intraday time.
 * DTE = calendar days to expiry. timeOfDay = fractional ET hour.
 *
 * Formula: T = dte/365 + (close - timeOfDay) / 8760
 *
 * At 9:30 AM with DTE=0: T = 0 + 6.5/8760 ≈ 0.000742 (6.5h remaining)
 * At 4:00 PM with DTE=0: T = 0 + 0/8760 = 0 (expired)
 * At 9:30 AM with DTE=1: T = 1/365 + 6.5/8760 ≈ 0.003482
 */
export function computeT(dte: number, timeOfDay: number): number {
  return dte / DAYS_PER_YEAR + (MARKET_CLOSE_HOUR - timeOfDay) / HOURS_PER_YEAR;
}

/**
 * Clamp a time value to market hours [9.5, 16.0].
 */
export function clampMarketHour(hour: number): number {
  return Math.max(MARKET_OPEN_HOUR, Math.min(hour, MARKET_CLOSE_HOUR));
}

/**
 * Current ET hour as fractional value in [0, 24).
 * Always returns actual wall-clock time — off-RTH callers must cope with
 * values outside market hours. The simulator's entry-time ticker relies on
 * this so weekend and after-hours theta decay is reflected in `computeT`.
 */
export function currentETHour(): number {
  const now = new Date();
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(now)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
  return Number(parts.hour) + Number(parts.minute) / 60;
}

/** Return ISO date string N months ago from today. */
export function dateMonthsAgo(months: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString().split("T")[0];
}

/** Return today's ISO date string. */
export function todayISO(): string {
  return new Date().toISOString().split("T")[0];
}

/**
 * Convert a T value (annualized time-to-expiry) to a Date.
 * T = dte/365 + (16 - hour)/8760, so total hours to expiry close = T * 8760.
 * @param t — annualized time-to-expiry
 * @param expiryCloseMs — millisecond timestamp of the expiry close (4 PM ET)
 */
export function tToDate(t: number, expiryCloseMs: number): Date {
  return new Date(expiryCloseMs - t * HOURS_PER_YEAR * 3600_000);
}

/**
 * Format an annualized time-to-expiry value as a human-readable remaining-time string.
 * t * 8760 = hours remaining. Picks the largest unit that yields a 1+ leading number.
 */
export function formatTimeRemaining(t: number): string {
  const totalHours = Math.max(0, t * HOURS_PER_YEAR);
  if (totalHours >= 24) {
    const days = Math.floor(totalHours / 24);
    const hours = Math.round(totalHours - days * 24);
    return `${days}d ${hours}h`;
  }
  if (totalHours >= 1) {
    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);
    return `${hours}h ${minutes}m`;
  }
  const minutes = Math.max(0, Math.round(totalHours * 60));
  return `${minutes}m`;
}

/** Return a date range: { from, to } where `to` is yesterday, `from` is N months prior (0 = from 2015). */
export function dateRange(months: number): { from: string; to: string } {
  const to = new Date();
  to.setDate(to.getDate() - 1);
  const from =
    months > 0
      ? new Date(to.getFullYear(), to.getMonth() - months, to.getDate())
      : new Date(2015, 0, 1);
  return {
    from: from.toISOString().split("T")[0],
    to: to.toISOString().split("T")[0],
  };
}
